import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import AnalyticsView from "@/components/AnalyticsView";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Users, Building2, BookOpen, TrendingUp, Search, MoreVertical, 
  Shield, User, Plus, Settings, Calendar, Euro, CheckCircle, Clock, XCircle,
  ExternalLink, Eye, Pencil, Trash2, Package, ToggleLeft, ToggleRight, BarChart3,
  Bell, FileText, Loader2, Save
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchBusinesses, setSearchBusinesses] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  // Read initial tab from URL query param
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "businesses");
  
  // Update activeTab when URL changes
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);
  
  // Firebase Push state
  const [firebaseBusinessId, setFirebaseBusinessId] = useState("");
  const [firebaseUploading, setFirebaseUploading] = useState(false);
  
  // Business settings state (for notification, redirect, recurring)
  const [businessSettings, setBusinessSettings] = useState({
    booking_confirmation_title: "",
    booking_confirmation_body: "",
    reminder_enabled: true,
    reminder_hours: [24],
    reminder_title: "",
    reminder_body: "",
    redirect_url_after_booking: "",
    recurring_appointments_enabled: false
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Create Business Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: "",
    description: "",
    email: "",
    phone: "",
    address: "",
    timezone: "Europe/Rome",
    slug: "",
    total_resources: 1,
    business_type: "appointment", // appointment, event, daily_rental
    // Owner account fields
    owner_email: "",
    owner_password: "",
    owner_name: ""
  });

  const BUSINESS_TYPES = [
    { value: "appointment", label: "🕐 Servizi su Appuntamento", desc: "Parrucchiere, Dentista, Consulente..." },
    { value: "event", label: "👥 Eventi/Attività di Gruppo", desc: "Escursioni, Corsi, Tour guidati..." },
    { value: "daily_rental", label: "🏖️ Noleggio a Giornata", desc: "Lido/Spiaggia, Noleggio barche..." }
  ];

  useEffect(() => {
    if (user && token) {
      fetchData();
    }
  }, [user, token]);

  // Load business settings when firebaseBusinessId changes
  useEffect(() => {
    if (firebaseBusinessId) {
      const biz = businesses.find(b => b.business_id === firebaseBusinessId);
      if (biz) {
        const ns = biz.notification_settings || {};
        setBusinessSettings({
          booking_confirmation_title: ns.booking_confirmation_title || "Prenotazione Confermata ✅",
          booking_confirmation_body: ns.booking_confirmation_body || "{service_name} - {booking_date}",
          reminder_enabled: ns.reminder_enabled !== false,
          reminder_hours: ns.reminder_hours || [24],
          reminder_title: ns.reminder_title || "Promemoria Appuntamento 🔔",
          reminder_body: ns.reminder_body || "Domani alle {booking_time}: {service_name}",
          redirect_url_after_booking: biz.redirect_url_after_booking || "",
          recurring_appointments_enabled: biz.recurring_appointments_enabled || false
        });
      }
    }
  }, [firebaseBusinessId, businesses]);

  const fetchData = async () => {
    try {
      // Base requests for all admin roles
      const requests = [
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/admin/businesses`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
      ];
      
      // Only super_admin can access users list
      if (user?.role === "super_admin") {
        requests.push(
          axios.get(`${API}/admin/users`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          })
        );
      }
      
      const responses = await Promise.all(requests);
      
      setStats(responses[0].data);
      setBusinesses(responses[1].data);
      if (user?.role === "super_admin" && responses[2]) {
        setUsers(responses[2].data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/admin/businesses`, newBusiness, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Attività creata con successo!");
      setCreateDialogOpen(false);
      setNewBusiness({
        name: "",
        description: "",
        email: "",
        phone: "",
        address: "",
        timezone: "Europe/Rome",
        slug: "",
        total_resources: 1,
        owner_email: "",
        owner_password: "",
        owner_name: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la creazione");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!confirm("Sei sicuro di voler eliminare questa attività? Tutti i dati associati verranno persi.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Attività eliminata!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleToggleBusinessActive = async (biz) => {
    const newStatus = !biz.is_active;
    try {
      await axios.put(`${API}/admin/businesses/${biz.business_id}`, {
        is_active: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success(newStatus ? "Attività attivata!" : "Attività disattivata!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    }
  };

  // Save business settings (notification, redirect, recurring)
  const handleSaveBusinessSettings = async () => {
    if (!firebaseBusinessId) {
      toast.error("Seleziona un'azienda");
      return;
    }
    
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/admin/business/${firebaseBusinessId}/settings`,
        {
          notification_settings: {
            booking_confirmation_title: businessSettings.booking_confirmation_title,
            booking_confirmation_body: businessSettings.booking_confirmation_body,
            reminder_enabled: businessSettings.reminder_enabled,
            reminder_hours: businessSettings.reminder_hours,
            reminder_title: businessSettings.reminder_title,
            reminder_body: businessSettings.reminder_body
          },
          redirect_url_after_booking: businessSettings.redirect_url_after_booking || null,
          recurring_appointments_enabled: businessSettings.recurring_appointments_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Impostazioni salvate con successo!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingSettings(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .slice(0, 30);
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-purple-100 text-purple-800",
      business_owner: "bg-blue-100 text-blue-800",
      staff: "bg-emerald-100 text-emerald-800",
      customer: "bg-slate-100 text-slate-600"
    };
    const labels = {
      super_admin: "Super Admin",
      business_owner: "Business Owner",
      staff: "Staff",
      customer: "Cliente"
    };
    return <Badge className={styles[role] || styles.customer}>{labels[role] || role}</Badge>;
  };

  const filteredBusinesses = businesses.filter(b => {
    if (!searchBusinesses) return true;
    const searchLower = searchBusinesses.toLowerCase();
    return (
      b.name?.toLowerCase().includes(searchLower) ||
      b.email?.toLowerCase().includes(searchLower) ||
      b.slug?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('admin.title')}
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">{t('admin.subtitle')}</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-business-btn" className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                <span className="sm:inline">{t('admin.businesses.add')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{t('admin.businesses.add')}</DialogTitle>
                <DialogDescription>
                  {t('business.settings.businessType')}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateBusiness} className="space-y-4">
                {/* Business Type Selection - FIRST CHOICE */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">{t('business.settings.businessType')} *</Label>
                  <div className="grid gap-2">
                    {BUSINESS_TYPES.map((type) => (
                      <div
                        key={type.value}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          newBusiness.business_type === type.value
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => setNewBusiness({ ...newBusiness, business_type: type.value })}
                        data-testid={`business-type-${type.value}`}
                      >
                        <div className="font-medium">{type.label}</div>
                        <div className="text-sm text-slate-500">{type.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="biz-name">Nome Attività *</Label>
                    <Input
                      id="biz-name"
                      value={newBusiness.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setNewBusiness({ 
                          ...newBusiness, 
                          name,
                          slug: newBusiness.slug || generateSlug(name)
                        });
                      }}
                      placeholder={newBusiness.business_type === "daily_rental" ? "Es. Lido Mare Blu" : newBusiness.business_type === "event" ? "Es. Escursioni Costa Amalfitana" : "Es. Salone Bella Vita"}
                      required
                      data-testid="new-business-name"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="biz-email">Email *</Label>
                  <Input
                    id="biz-email"
                    type="email"
                    value={newBusiness.email}
                    onChange={(e) => setNewBusiness({ ...newBusiness, email: e.target.value })}
                    placeholder="info@attivita.it"
                    required
                    data-testid="new-business-email"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="biz-phone">Telefono</Label>
                    <Input
                      id="biz-phone"
                      value={newBusiness.phone}
                      onChange={(e) => setNewBusiness({ ...newBusiness, phone: e.target.value })}
                      placeholder="+39 02 1234567"
                      data-testid="new-business-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="biz-resources">Posti/Risorse</Label>
                    <Input
                      id="biz-resources"
                      type="number"
                      min="1"
                      value={newBusiness.total_resources}
                      onChange={(e) => setNewBusiness({ ...newBusiness, total_resources: parseInt(e.target.value) || 1 })}
                      data-testid="new-business-resources"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="biz-address">Indirizzo</Label>
                  <Input
                    id="biz-address"
                    value={newBusiness.address}
                    onChange={(e) => setNewBusiness({ ...newBusiness, address: e.target.value })}
                    placeholder="Via Roma 1, Milano"
                    data-testid="new-business-address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="biz-slug">URL Prenotazione</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">/book/</span>
                    <Input
                      id="biz-slug"
                      value={newBusiness.slug}
                      onChange={(e) => setNewBusiness({ ...newBusiness, slug: generateSlug(e.target.value) })}
                      placeholder="salone-bella-vita"
                      data-testid="new-business-slug"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="biz-description">Descrizione</Label>
                  <Textarea
                    id="biz-description"
                    value={newBusiness.description}
                    onChange={(e) => setNewBusiness({ ...newBusiness, description: e.target.value })}
                    placeholder="Breve descrizione dell'attività..."
                    rows={2}
                    data-testid="new-business-description"
                  />
                </div>

                {/* Owner Account Section */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Account Proprietario (opzionale)
                  </h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Crea un account per permettere al proprietario di gestire la propria attività
                  </p>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="owner-name">Nome Proprietario</Label>
                      <Input
                        id="owner-name"
                        value={newBusiness.owner_name}
                        onChange={(e) => setNewBusiness({ ...newBusiness, owner_name: e.target.value })}
                        placeholder="Mario Rossi"
                        data-testid="new-business-owner-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="owner-email">Email Proprietario</Label>
                      <Input
                        id="owner-email"
                        type="email"
                        value={newBusiness.owner_email}
                        onChange={(e) => setNewBusiness({ ...newBusiness, owner_email: e.target.value })}
                        placeholder="mario@azienda.it"
                        data-testid="new-business-owner-email"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="owner-password">Password</Label>
                      <Input
                        id="owner-password"
                        type="password"
                        value={newBusiness.owner_password}
                        onChange={(e) => setNewBusiness({ ...newBusiness, owner_password: e.target.value })}
                        placeholder="Almeno 8 caratteri"
                        data-testid="new-business-owner-password"
                      />
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="submit" disabled={creating} data-testid="submit-new-business">
                    {creating ? "Creazione..." : "Crea Attività"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-slate-100 shadow-sm" data-testid="admin-stat-businesses">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">{t('admin.stats.businesses')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{stats?.total_businesses || 0}</p>
                </div>
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm" data-testid="admin-stat-bookings">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">{t('admin.stats.bookings')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{stats?.total_bookings || 0}</p>
                </div>
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue - Only visible to super_admin */}
          {user?.role === "super_admin" && (
            <Card className="border-slate-100 shadow-sm" data-testid="admin-stat-revenue">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">{t('admin.stats.revenue')}</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">€{(stats?.total_revenue || 0).toFixed(2)}</p>
                  </div>
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-100 shadow-sm" data-testid="admin-stat-users">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">{t('admin.stats.users')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{stats?.total_users || 0}</p>
                </div>
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Businesses and Analytics */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="businesses" data-testid="tab-businesses" className="text-sm">
              <Building2 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('dashboard.businesses')}</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-sm">
              <BarChart3 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('dashboard.analytics')}</span>
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="text-sm">
              <Settings className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Impostazioni</span>
            </TabsTrigger>
          </TabsList>

          {/* Businesses Tab */}
          <TabsContent value="businesses" className="mt-4 sm:mt-6">
            <Card className="border-slate-100">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{t('admin.businesses.title')}</CardTitle>
                    <CardDescription>{t('admin.businesses.subtitle')}</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Cerca attività..."
                      value={searchBusinesses}
                      onChange={(e) => setSearchBusinesses(e.target.value)}
                      className="pl-10"
                      data-testid="admin-business-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredBusinesses.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Nessuna attività creata</p>
                    <Button onClick={() => setCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Crea la prima attività
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBusinesses.map((biz) => (
                      <Card 
                        key={biz.business_id} 
                        className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                        data-testid={`business-card-${biz.business_id}`}
                      >
                        <CardContent className="p-4 sm:p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{biz.name}</h3>
                                <p className="text-xs text-slate-500 font-mono truncate">/book/{biz.slug}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                              <Badge className={`text-xs ${biz.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                                {biz.is_active ? "Attiva" : "Inattiva"}
                              </Badge>
                              <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                                {biz.business_type === "event" ? "👥 Eventi" : biz.business_type === "daily_rental" ? "🏖️ Noleggio" : "🕐 App."}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="space-y-1 mb-4">
                            <p className="text-xs sm:text-sm text-slate-600 truncate">{biz.email}</p>
                            {biz.phone && <p className="text-xs sm:text-sm text-slate-500 truncate hidden sm:block">{biz.phone}</p>}
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                            <div className="p-1.5 sm:p-2 bg-slate-50 rounded">
                              <p className="text-base sm:text-lg font-bold text-slate-900">{biz.stats?.total_services || 0}</p>
                              <p className="text-xs text-slate-500">Servizi</p>
                            </div>
                            <div className="p-1.5 sm:p-2 bg-slate-50 rounded">
                              <p className="text-base sm:text-lg font-bold text-slate-900">{biz.stats?.total_bookings || 0}</p>
                              <p className="text-xs text-slate-500">Prenot.</p>
                            </div>
                            {/* Revenue - Only visible to super_admin */}
                            {user?.role === "super_admin" && biz.stats?.total_revenue !== undefined && (
                              <div className="p-1.5 sm:p-2 bg-slate-50 rounded">
                                <p className="text-base sm:text-lg font-bold text-emerald-600">€{(biz.stats?.total_revenue || 0).toFixed(0)}</p>
                                <p className="text-xs text-slate-500">Ricavi</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button 
                              className="flex-1 text-xs sm:text-sm"
                              size="sm"
                              onClick={() => navigate(`/admin/business/${biz.business_id}`)}
                              data-testid={`manage-business-${biz.business_id}`}
                            >
                              <Settings className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                              <span className="hidden sm:inline">Gestisci</span>
                              <span className="sm:hidden">Apri</span>
                            </Button>
                            <a href={`/book/${biz.slug}`} target="_blank" rel="noopener noreferrer" className="hidden sm:block">
                              <Button variant="outline" size="icon" className="h-8 w-8">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </a>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/admin/business/${biz.business_id}`)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Gestisci
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(`/book/${biz.slug}`, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Apri Pagina Prenotazione
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/book/${biz.slug}`);
                                  toast.success("Link copiato!");
                                }}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Copia Link
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  const iframeCode = `<iframe src="${window.location.origin}/book/${biz.slug}" width="100%" height="800" frameborder="0" style="border: none; border-radius: 8px;" title="Prenota un appuntamento"></iframe>`;
                                  navigator.clipboard.writeText(iframeCode);
                                  toast.success("Codice iframe copiato!");
                                }}>
                                  <Package className="h-4 w-4 mr-2" />
                                  Copia Codice Iframe
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleBusinessActive(biz)}>
                                  {biz.is_active ? (
                                    <><ToggleRight className="h-4 w-4 mr-2 text-green-500" />Disattiva</>
                                  ) : (
                                    <><ToggleLeft className="h-4 w-4 mr-2 text-slate-400" />Attiva</>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => handleDeleteBusiness(biz.business_id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-4 sm:mt-6">
            <AnalyticsView isAdmin={true} showAllBusinesses={true} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-4 sm:mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Bell className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle>Firebase Push Notifications</CardTitle>
                    <CardDescription>
                      Configura Firebase Cloud Messaging per le aziende che gestisci
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Seleziona Azienda</Label>
                  <Select value={firebaseBusinessId} onValueChange={setFirebaseBusinessId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona un'azienda..." />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map(b => (
                        <SelectItem key={b.business_id} value={b.business_id}>
                          {b.name} {b.firebase_project_id && <span className="text-green-600 ml-2">✓</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {firebaseBusinessId && (
                  <div className="space-y-4">
                    <div>
                      <Label>Firebase Service Account (JSON)</Label>
                      <p className="text-sm text-slate-500 mb-2">
                        Firebase Console → Impostazioni → Account di servizio → Genera nuova chiave privata
                      </p>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-primary transition-colors">
                        <input
                          type="file"
                          id="firebase-json-upload-admin"
                          accept=".json"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !firebaseBusinessId) return;
                            
                            const reader = new FileReader();
                            reader.onload = async (event) => {
                              try {
                                const jsonContent = event.target?.result;
                                const parsed = JSON.parse(jsonContent);
                                if (!parsed.project_id || !parsed.private_key) {
                                  toast.error("File JSON non valido");
                                  return;
                                }
                                
                                setFirebaseUploading(true);
                                try {
                                  const res = await axios.put(
                                    `${API}/admin/business/${firebaseBusinessId}/firebase-config`,
                                    { service_account_json: jsonContent },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`Firebase configurato! Progetto: ${res.data.project_id}`);
                                  fetchData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Errore nel salvataggio");
                                } finally {
                                  setFirebaseUploading(false);
                                }
                              } catch (err) {
                                toast.error("File JSON non valido");
                              }
                            };
                            reader.readAsText(file);
                          }}
                        />
                        <label htmlFor="firebase-json-upload-admin" className="cursor-pointer">
                          <div className="flex flex-col items-center gap-2">
                            {firebaseUploading ? (
                              <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            ) : (
                              <FileText className="h-8 w-8 text-slate-400" />
                            )}
                            <span className="text-sm text-slate-600">
                              Clicca per caricare <strong>serviceAccountKey.json</strong>
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {businesses.find(b => b.business_id === firebaseBusinessId)?.firebase_project_id && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-green-800">
                          Firebase configurato - Progetto: {businesses.find(b => b.business_id === firebaseBusinessId)?.firebase_project_id}
                        </span>
                      </div>
                    )}
                    
                    {/* Notification Text Settings */}
                    <div className="border rounded-lg p-4 space-y-4 mt-4">
                      <Label className="font-semibold">Testo Notifiche</Label>
                      
                      <div>
                        <Label className="text-sm">Titolo conferma prenotazione</Label>
                        <Input
                          placeholder="Prenotazione Confermata ✅"
                          value={businessSettings.booking_confirmation_title}
                          onChange={(e) => setBusinessSettings({...businessSettings, booking_confirmation_title: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Testo conferma (usa {"{service_name}"}, {"{booking_date}"})</Label>
                        <Input
                          placeholder="{service_name} - {booking_date}"
                          value={businessSettings.booking_confirmation_body}
                          onChange={(e) => setBusinessSettings({...businessSettings, booking_confirmation_body: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* Reminder Settings */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <Label className="font-semibold">Promemoria</Label>
                      
                      <div className="flex items-center gap-3">
                        <Switch 
                          id="reminder-enabled-admin" 
                          checked={businessSettings.reminder_enabled}
                          onCheckedChange={(checked) => setBusinessSettings({...businessSettings, reminder_enabled: checked})}
                        />
                        <label htmlFor="reminder-enabled-admin" className="text-sm">Abilita promemoria</label>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Invia promemoria (ore prima)</Label>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {[24, 12, 6, 2, 1].map(h => (
                            <label key={h} className="flex items-center gap-1 text-sm">
                              <input 
                                type="checkbox" 
                                checked={businessSettings.reminder_hours.includes(h)}
                                onChange={(e) => {
                                  const hours = e.target.checked 
                                    ? [...businessSettings.reminder_hours, h]
                                    : businessSettings.reminder_hours.filter(x => x !== h);
                                  setBusinessSettings({...businessSettings, reminder_hours: hours});
                                }}
                                className="rounded" 
                              />
                              {h}h
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm">Titolo promemoria</Label>
                        <Input
                          placeholder="Promemoria Appuntamento 🔔"
                          value={businessSettings.reminder_title}
                          onChange={(e) => setBusinessSettings({...businessSettings, reminder_title: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Testo promemoria</Label>
                        <Input
                          placeholder="Domani alle {booking_time}: {service_name}"
                          value={businessSettings.reminder_body}
                          onChange={(e) => setBusinessSettings({...businessSettings, reminder_body: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    {/* URL Redirect */}
                    <div className="border rounded-lg p-4">
                      <Label className="font-semibold">URL Redirect dopo prenotazione</Label>
                      <p className="text-xs text-slate-500 mb-2">Il cliente verrà reindirizzato a questo URL dopo aver completato la prenotazione</p>
                      <Input
                        placeholder="https://tuosito.com/grazie"
                        value={businessSettings.redirect_url_after_booking}
                        onChange={(e) => setBusinessSettings({...businessSettings, redirect_url_after_booking: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Recurring Appointments */}
                    <div className="border rounded-lg p-4">
                      <Label className="font-semibold">Appuntamenti Ricorrenti</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Switch 
                          id="recurring-enabled-admin" 
                          checked={businessSettings.recurring_appointments_enabled}
                          onCheckedChange={(checked) => setBusinessSettings({...businessSettings, recurring_appointments_enabled: checked})}
                        />
                        <label htmlFor="recurring-enabled-admin" className="text-sm">
                          Permetti ai clienti di prenotare appuntamenti ricorrenti
                        </label>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full" 
                      onClick={handleSaveBusinessSettings}
                      disabled={savingSettings}
                    >
                      {savingSettings ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Salva Impostazioni Azienda
                    </Button>
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-blue-900 mb-2">Importante</h4>
                  <p className="text-sm text-blue-800">
                    Per iOS, carica la <strong>APNs Auth Key</strong> in Firebase Console → Cloud Messaging → Configurazione app Apple.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
